import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, CATEGORY, BACKEND } from "../lib/api";
import { store, load, clear, K_CONSOLE } from "../lib/storage";
import { useRoom, useCountdown } from "../lib/useRoom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Loader2, Wifi, WifiOff, Zap, Send, Download, Mail, Trophy, Plus, Trash2, Pencil, Play, Square, LogOut, Copy, Archive, ArchiveRestore, Eye, ShieldCheck } from "lucide-react";

const PHASES = ["lobby", "reparto", "combinacion", "skeptic", "blueprint", "producir", "pitch", "cierre", "closed"];
const PLAY = ["reparto", "combinacion", "skeptic", "blueprint", "producir", "pitch", "cierre"];
const PHASE_LABEL = { lobby: "Lobby", reparto: "The Deal", combinacion: "The Combination", skeptic: "The Skeptic", blueprint: "The Blueprint", producir: "Produce", pitch: "Pitch", cierre: "Closing", closed: "Closed" };
const PRESET_MSGS = ["5 minutes left", "Move to the Blueprint", "Drop your markers", "Pitch in 10 minutes"];

export default function Console() {
  const [user, setUser] = useState(undefined); // undefined=checking, null=logged out
  const [selected, setSelected] = useState(null); // {code, pin}

  // OAuth callback: exchange session_id (URL fragment) -> token, once.
  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) {
        const sid = new URLSearchParams(hash.slice(1)).get("session_id");
        try {
          const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
          localStorage.setItem("dig_admin_token", data.session_token);
          localStorage.setItem("dig_admin_user", JSON.stringify(data));
          setUser(data);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Access denied.");
          setUser(null);
        }
        window.history.replaceState({}, "", "/console");
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        localStorage.removeItem("dig_admin_token");
        setUser(null);
      }
    };
    run();
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("dig_admin_token");
    localStorage.removeItem("dig_admin_user");
    setUser(null); setSelected(null);
  };

  if (user === undefined) return <div className="min-h-screen bg-dig-paper flex items-center justify-center"><Loader2 className="animate-spin text-dig-blue" size={40} /></div>;
  if (!user) return <Login />;
  if (!selected) return <Admin user={user} onOpen={setSelected} onLogout={logout} />;
  return <ConsoleDash code={selected.code} pin={selected.pin} onExit={() => setSelected(null)} />;
}

function Login() {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/console";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen bg-dig-paper text-dig-harbour flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 dig-card-shadow text-center">
        <div className="flex justify-center mb-6"><img src="/dig/DIG-Logo-Horizontal.svg" alt="DIG" style={{ height: 48, transform: "rotate(-2deg)" }} /></div>
        <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour/50 mb-2">FACILITATOR CONSOLE</div>
        <h1 className="text-2xl font-black mb-2">Facilitator access</h1>
        <p className="text-sm text-dig-harbour/60 mb-6">Sign in with your Google account. Only <b className="text-dig-blue">@reshapers.co</b> accounts.</p>
        <button data-testid="google-signin-btn" onClick={signIn}
          className="w-full h-12 rounded-full bg-dig-harbour text-white font-bold flex items-center justify-center gap-3 hover:brightness-110 transition-[filter]">
          <ShieldCheck size={18} /> Sign in with Google
        </button>
      </div>
    </div>
  );
}

const ADMIN_PHASE = { lobby: "Lobby", reparto: "The Deal", combinacion: "The Combination", skeptic: "The Skeptic", blueprint: "The Blueprint", producir: "Produce", pitch: "Pitch", cierre: "Closing", closed: "Closed" };

function Admin({ user, onOpen, onLogout }) {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("active");
  const loadSessions = useCallback(async () => {
    try { const { data } = await api.get("/admin/sessions"); setSessions(data.sessions); } catch (e) { toast.error("Could not load."); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const create = async () => { setBusy(true); try { const { data } = await api.post("/admin/sessions", {}); toast.success(`Session ${data.code} created`); onOpen({ code: data.code, pin: data.pin }); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  const duplicate = async (code) => { try { const { data } = await api.post(`/admin/sessions/${code}/duplicate`); toast.success(`Duplicated → ${data.code}`); loadSessions(); } catch (e) { toast.error("Error"); } };
  const setStatus = async (code, status) => { try { await api.post(`/admin/sessions/${code}/status`, { status }); loadSessions(); } catch (e) { toast.error("Error"); } };

  const shown = (sessions || []).filter((s) => filter === "all" ? true : filter === "archived" ? s.status === "archived" : s.status !== "archived");

  return (
    <div className="min-h-screen bg-dig-paper text-dig-harbour">
      <header className="bg-white border-b border-border flex items-center gap-4 px-6 h-16">
        <img src="/dig/DIG-Logo-Horizontal.svg" alt="DIG" style={{ height: 30, transform: "rotate(-2deg)" }} />
        <div className="w-px h-6 bg-border" />
        <div className="text-xs font-bold tracking-[0.16em]">FACILITATOR PANEL</div>
        <div className="flex-1" />
        <div className="text-sm text-dig-harbour/60">{user.name || user.email}</div>
        <span className="text-[10px] font-bold bg-dig-turquoise-tint text-dig-blue rounded-full px-2 py-1">@reshapers.co</span>
        <Button data-testid="admin-logout-btn" onClick={onLogout} variant="outline" size="sm" className="rounded-full"><LogOut size={15} className="mr-1" />Log out</Button>
      </header>

      <main className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-3xl font-black">Sessions</h1>
            <p className="text-dig-harbour/50 text-sm">Create, open, duplicate and archive game sessions.</p>
          </div>
          <Button data-testid="admin-new-session-btn" onClick={create} disabled={busy} className="rounded-full bg-dig-blue text-white font-bold h-12 px-6"><Plus size={18} className="mr-1" />New session</Button>
        </div>

        <div className="flex gap-2 mb-4">
          {["active", "archived", "all"].map((f) => (
            <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold ${filter === f ? "bg-dig-harbour text-white" : "bg-white border border-border text-dig-harbour/60"}`}>
              {f === "active" ? "Active" : f === "archived" ? "Archived" : "All"}
            </button>
          ))}
        </div>

        {sessions === null ? <Loader2 className="animate-spin text-dig-blue mx-auto mt-10" size={32} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.length === 0 ? <p className="text-dig-harbour/50 col-span-full">No sessions.</p> : shown.map((s) => (
              <div key={s.code} data-testid={`admin-session-${s.code}`} className="bg-white rounded-2xl border border-border p-5 dig-card-shadow flex flex-col gap-2" style={{ opacity: s.status === "archived" ? 0.6 : 1 }}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-dig text-2xl font-bold tracking-[0.15em]">{s.code}</span>
                  <span className="text-[10px] font-bold rounded-full px-2 py-1" style={{ background: s.status === "active" ? "var(--dig-turquoise-tint)" : s.status === "archived" ? "var(--dig-harbour-tint)" : "var(--dig-coral-tint)", color: s.status === "active" ? "#1B7FA8" : "#4A6570" }}>{(s.status || "").toUpperCase()}</span>
                </div>
                <div className="text-sm text-dig-harbour/60">{ADMIN_PHASE[s.phase] || s.phase} · {s.team_count} teams · {s.player_count} players</div>
                <div className="text-[11px] text-dig-harbour/40">PIN {s.pin}{s.created_by_email ? ` · ${s.created_by_email}` : ""}</div>
                <div className="flex-1" />
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button data-testid={`admin-open-${s.code}`} onClick={() => onOpen({ code: s.code, pin: s.pin })} size="sm" className="rounded-full bg-dig-blue text-white text-xs h-8"><Eye size={13} className="mr-1" />Open</Button>
                  <Button data-testid={`admin-duplicate-${s.code}`} onClick={() => duplicate(s.code)} size="sm" variant="outline" className="rounded-full text-xs h-8"><Copy size={13} className="mr-1" />Duplicate</Button>
                  {s.status === "archived" ? (
                    <Button data-testid={`admin-restore-${s.code}`} onClick={() => setStatus(s.code, "active")} size="sm" variant="outline" className="rounded-full text-xs h-8"><ArchiveRestore size={13} className="mr-1" />Restore</Button>
                  ) : (
                    <Button data-testid={`admin-archive-${s.code}`} onClick={() => setStatus(s.code, "archived")} size="sm" variant="outline" className="rounded-full text-xs h-8 border-dig-coral text-dig-coral"><Archive size={13} className="mr-1" />Archive</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function ConsoleDash({ code, pin, onExit }) {
  const { snap, connected } = useRoom(code, { role: "console", pin });
  const H = { headers: { "X-Console-Pin": pin } };
  const session = snap?.session;
  const teams = snap?.teams || [];
  const players = snap?.players || [];
  const call = useCallback(async (fn) => { try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } }, []);
  const setPhase = (phase) => call(() => api.post(`/session/${code}/phase`, { phase }, H));
  const timer = session?.active_timer;

  if (!session) return <div className="min-h-screen bg-dig-paper flex items-center justify-center"><Loader2 className="animate-spin text-dig-blue" size={40} /></div>;

  const phaseIdx = PHASES.indexOf(session.phase);
  const nextPhase = phaseIdx >= 0 && phaseIdx < PHASES.length - 1 ? PHASES[phaseIdx + 1] : null;
  const isLobby = session.phase === "lobby";
  const statusActive = !isLobby && session.phase !== "closed";

  return (
    <div className="min-h-screen bg-dig-paper text-dig-harbour flex flex-col">
      {/* header */}
      <header className="bg-white border-b border-border flex items-center gap-4 px-5 h-16 flex-none">
        <img src="/dig/DIG-Logo-Horizontal.svg" alt="DIG" style={{ height: 30, transform: "rotate(-2deg)" }} />
        <div className="w-px h-6 bg-border" />
        <div className="text-xs font-bold tracking-[0.16em] hidden md:block">FACILITATOR CONSOLE</div>
        <div className="rounded-full px-3 py-1.5 text-[0.65rem] font-bold tracking-widest" style={{ background: statusActive ? "#FDBB2F" : "var(--dig-harbour-tint)", color: statusActive ? "#12333F" : "#4A6570" }}>
          {isLobby ? "NOT STARTED" : PHASE_LABEL[session.phase]?.toUpperCase()}
        </div>
        <div className="flex-1" />
        <TimerBlock timer={timer} onStop={() => call(() => api.post(`/session/${code}/timer`, { action: "stop" }, H))} onPlus={() => call(() => api.post(`/session/${code}/timer`, { action: "start", label: "custom", duration: 300 }, H))} />
        {connected ? <Wifi size={18} className="text-dig-turquoise" /> : <WifiOff size={18} className="text-dig-coral" />}
        {isLobby ? (
          <Button data-testid="start-session-btn" onClick={() => setPhase("reparto")} className="rounded-full bg-dig-yellow text-dig-harbour font-bold">Start session</Button>
        ) : nextPhase ? (
          <Button data-testid="header-next-phase" onClick={() => setPhase(nextPhase)} className="rounded-full bg-dig-yellow text-dig-harbour font-bold">Next →</Button>
        ) : null}
        <Button data-testid="console-exit-btn" onClick={onExit} variant="outline" size="sm" className="rounded-full">Exit</Button>
      </header>

      {/* light phase bar */}
      <div className="bg-white/60 border-b border-border px-5 py-2 flex items-center gap-2 overflow-x-auto dig-scroll">
        {PHASES.map((p) => (
          <button key={p} data-testid={`phase-jump-${p}`} onClick={() => setPhase(p)}
            className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap shrink-0 transition-colors ${session.phase === p ? "bg-dig-harbour text-white" : "bg-dig-harbour-tint text-dig-harbour/60 hover:bg-dig-harbour/10"}`}>
            {PHASE_LABEL[p]}
          </button>
        ))}
      </div>

      <Tabs defaultValue="room" className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-5 mt-3 bg-white self-start">
          <TabsTrigger value="room" data-testid="tab-room">Room</TabsTrigger>
          <TabsTrigger value="twists" data-testid="tab-twists">Twists</TabsTrigger>
          <TabsTrigger value="timers" data-testid="tab-timers">Timers</TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">Cards</TabsTrigger>
          <TabsTrigger value="invest" data-testid="tab-invest">Investment</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">Export</TabsTrigger>
        </TabsList>

        <TabsContent value="room" className="flex-1 min-h-0 m-0">
          <RoomView code={code} pin={pin} H={H} teams={teams} players={players} session={session} call={call} nextPhase={nextPhase} setPhase={setPhase} />
        </TabsContent>
        <TabsContent value="twists" className="p-5"><TwistsPanel code={code} H={H} session={session} call={call} /></TabsContent>
        <TabsContent value="timers" className="p-5"><TimersPanel code={code} H={H} session={session} call={call} /></TabsContent>
        <TabsContent value="cards" className="p-5"><CardsPanel code={code} H={H} /></TabsContent>
        <TabsContent value="invest" className="p-5"><Leaderboard teams={teams} session={session} code={code} H={H} call={call} /></TabsContent>
        <TabsContent value="export" className="p-5"><ExportPanel code={code} H={H} session={session} call={call} /></TabsContent>
      </Tabs>
    </div>
  );
}

function TimerBlock({ timer, onStop, onPlus }) {
  const remaining = useCountdown(timer);
  const running = timer?.running;
  return (
    <div className="flex items-center gap-2 bg-dig-harbour rounded-xl px-3 py-1.5">
      <span className="text-white font-mono-dig text-xl font-bold tabular-nums">{running ? `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}` : "--:--"}</span>
      {running ? <button data-testid="global-timer-stop" onClick={onStop} className="text-white/70 hover:text-white bg-white/10 rounded-md px-2 py-1"><Square size={12} /></button>
        : <button data-testid="global-timer-5" onClick={onPlus} className="text-white/80 text-xs font-semibold bg-white/10 rounded-md px-2 py-1">+5 min</button>}
    </div>
  );
}

/* ---------- Room (mockup layout) ---------- */
function RoomView({ code, pin, H, teams, players, session, call, nextPhase, setPhase }) {
  const origin = window.location.origin;
  const teamUrl = `${origin}/team?code=${code}`;
  const playUrl = `${origin}/play?code=${code}`;
  const seg = (t) => {
    const idx = PLAY.indexOf(t.phase);
    return PLAY.map((_, i) => (idx >= 0 && i <= idx ? t.color_hex : "var(--dig-harbour-tint)"));
  };
  return (
    <div className="flex-1 flex min-h-0 h-full">
      {/* teams */}
      <div className="flex-1 p-5 flex flex-col gap-3 min-w-0 overflow-y-auto dig-scroll">
        <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour/50">TEAMS</div>
        {teams.length === 0 ? (
          <p className="text-dig-harbour/50 text-sm">No teams yet. Share the code <b className="font-mono-dig text-dig-blue">{code}</b> or the QR.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {teams.map((t) => (
              <div key={t.id} data-testid={`room-team-${t.id}`} className="bg-white border border-border rounded-xl p-4 flex flex-col gap-2.5" style={{ opacity: t.online ? 1 : 0.55 }}>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full flex-none" style={{ background: t.color_hex }} />
                  <span className="font-bold text-[15px] flex-1 truncate">{t.name}</span>
                  {session.winner_team_id === t.id ? <Trophy size={15} className="text-dig-yellow" /> : null}
                  <span className="text-[10px] font-semibold text-dig-harbour/50">{t.online ? "ONLINE" : "OFFLINE"}</span>
                </div>
                <div className="flex gap-1">{seg(t).map((bg, i) => <div key={i} className="flex-1 h-[5px] rounded-full" style={{ background: bg, transition: "background .2s" }} />)}</div>
                <div className="text-[13px] font-semibold text-dig-harbour">{PHASE_LABEL[t.phase] || t.phase}{t.phase === "blueprint" ? ` · box ${(t.blueprint_box || 0) + 1}/8` : ""}</div>
                {t.offer_name ? <div className="bg-dig-yellow-tint rounded-full px-2.5 py-0.5 text-[11px] font-semibold self-start max-w-full truncate">{t.offer_name}</div> : null}
                <div className="text-[11px] text-dig-harbour/50">{t.artifact?.url ? "📷 artifact" : t.artifact?.link ? "🔗 artifact" : "no artifact"} · hand {(t.hand || []).length}/4 {t.hand_locked ? "🔒" : ""}</div>
                <div className="flex-1" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  <InvestInput code={code} H={H} team={t} call={call} />
                  <button data-testid={`pitch-trigger-${t.id}`} onClick={() => call(() => api.post(`/session/${code}/pitch`, { team_id: t.id }, H))} className="text-[12px] font-semibold text-dig-coral border-2 border-dig-coral rounded-full px-2.5 py-1 flex items-center gap-1 hover:bg-dig-coral-tint transition-colors"><Zap size={12} />90s</button>
                  <button data-testid={`winner-${t.id}`} onClick={() => call(() => api.post(`/session/${code}/winner`, { team_id: t.id }, H))} className="text-dig-harbour/50 hover:text-dig-yellow"><Trophy size={15} /></button>
                  <button data-testid={`push-team-${t.id}`} onClick={() => { const i = PHASES.indexOf(t.phase); if (i < PHASES.length - 1) call(() => api.post(`/session/${code}/team-phase`, { team_id: t.id, phase: PHASES[i + 1] }, H)); }}
                    className="ml-auto text-[12px] font-semibold text-dig-harbour border-2 border-dig-harbour rounded-full px-3 py-1 hover:bg-dig-harbour-tint transition-colors">Push →</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-3 bg-white border border-border rounded-xl px-4 py-3">
          <span className="text-sm text-dig-harbour/60 flex-1">Push all tables to the next phase.</span>
          {nextPhase ? <Button data-testid="push-all-btn" onClick={() => setPhase(nextPhase)} className="rounded-full bg-dig-harbour text-white font-bold">Push all → {PHASE_LABEL[nextPhase]}</Button> : null}
        </div>
      </div>

      {/* sidebar: QR + messages */}
      <div className="w-[340px] flex-none border-l border-border bg-white p-5 flex flex-col gap-4 overflow-y-auto dig-scroll">
        <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour/50">JOIN</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col items-center gap-2 border border-border rounded-xl p-3" data-testid="qr-team">
            <QRCodeSVG value={teamUrl} size={112} bgColor="#ffffff" fgColor="#12333F" level="M" />
            <span className="text-[11px] font-bold tracking-widest text-dig-blue">TEAMS</span>
          </div>
          <div className="flex flex-col items-center gap-2 border border-border rounded-xl p-3" data-testid="qr-play">
            <QRCodeSVG value={playUrl} size={112} bgColor="#ffffff" fgColor="#12333F" level="M" />
            <span className="text-[11px] font-bold tracking-widest text-dig-coral">PLAYERS</span>
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] tracking-widest text-dig-harbour/40">ROOM CODE</div>
          <div className="font-mono-dig text-3xl font-bold tracking-[0.2em] text-dig-harbour">{code}</div>
          <div className="text-[10px] tracking-widest text-dig-harbour/40 mt-1">PIN {pin} · {players.length} players</div>
        </div>
        <div className="border-t border-border pt-4"><MessagesPanel code={code} H={H} teams={teams} session={session} call={call} /></div>
      </div>
    </div>
  );
}

function InvestInput({ code, H, team, call }) {
  const [v, setV] = useState(team.investment_received || 0);
  useEffect(() => { setV(team.investment_received || 0); }, [team.investment_received]);
  return (
    <div className="flex items-center gap-1">
      <Input data-testid={`invest-input-${team.id}`} type="number" value={v} onChange={(e) => setV(e.target.value)} className="h-7 w-16 text-xs px-2" />
      <button data-testid={`invest-save-${team.id}`} onClick={() => call(() => api.post(`/session/${code}/investment`, { team_id: team.id, amount: Number(v) }, H))} className="h-7 rounded-full bg-dig-turquoise text-dig-harbour text-[11px] font-bold px-2">$</button>
    </div>
  );
}

function MessagesPanel({ code, H, teams, session, call }) {
  const [msg, setMsg] = useState("");
  const [target, setTarget] = useState("all");
  const send = (text) => call(async () => { await api.post(`/session/${code}/message`, { text, team_id: target === "all" ? null : target }, H); toast.success("Sent"); });
  return (
    <>
      <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour/50 mb-3">MESSAGES TO TABLES</div>
      <Select value={target} onValueChange={setTarget}>
        <SelectTrigger data-testid="msg-target" className="mb-3 h-9"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All teams</SelectItem>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESET_MSGS.map((m) => <button key={m} data-testid={`preset-msg-${m}`} onClick={() => send(m)} className="bg-dig-harbour-tint rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-dig-harbour/10">{m}</button>)}
      </div>
      <div className="flex gap-2">
        <Input data-testid="msg-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Custom message…" className="h-9" />
        <Button data-testid="msg-send" onClick={() => { send(msg); setMsg(""); }} disabled={!msg.trim()} className="rounded-full bg-dig-blue text-white shrink-0 h-9"><Send size={15} /></Button>
      </div>
      <div className="text-[11px] text-dig-harbour/40 mt-3">{session.message ? `Sent: "${session.message.text}"` : "Nothing sent yet."}</div>
    </>
  );
}

/* ---------- Twists ---------- */
function TwistsPanel({ code, H, session, call }) {
  const [twists, setTwists] = useState([]);
  const [custom, setCustom] = useState("");
  useEffect(() => { api.get(`/session/${code}/cards`).then(({ data }) => setTwists(data.grouped?.TWIST || [])).catch(() => {}); }, [code]);
  const push = (t) => call(async () => { await api.post(`/session/${code}/twist`, { code: t.code, es: t.es, en: t.en }, H); toast.success("Twist sent"); });
  const pushCustom = () => call(async () => { await api.post(`/session/${code}/twist`, { custom, es: custom, en: custom }, H); toast.success("Twist sent"); });
  const clearT = () => call(async () => { await api.post(`/session/${code}/twist`, { clear: true }, H); toast.success("Twist cleared"); });
  return (
    <div>
      {session.active_twist ? (
        <div className="bg-dig-harbour text-white rounded-2xl p-4 mb-5 flex items-center justify-between">
          <div><span className="font-bold tracking-widest text-xs text-dig-yellow">ACTIVE TWIST</span><p className="font-bold text-lg">{session.active_twist.es || session.active_twist.custom}</p></div>
          <Button data-testid="twist-clear-btn" onClick={clearT} className="rounded-full bg-dig-yellow text-dig-harbour font-bold">Clear</Button>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {twists.map((t) => (
          <button key={t.id} data-testid={`twist-${t.code}`} onClick={() => push(t)} className="text-left bg-white hover:dig-card-lift dig-card-shadow rounded-xl p-4 transition-shadow" style={{ borderLeft: "5px solid #12333F" }}>
            <span className="font-mono-dig text-xs text-dig-harbour/40">{t.code}</span>
            <p className="font-bold mt-1">{t.es || <em className="text-dig-harbour/40">Blank</em>}</p>
            <p className="text-xs text-dig-harbour/40 mt-0.5">{t.en}</p>
          </button>
        ))}
      </div>
      <div className="mt-5 flex gap-2 max-w-xl">
        <Input data-testid="twist-custom-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom twist…" />
        <Button data-testid="twist-custom-push" onClick={pushCustom} disabled={!custom.trim()} className="rounded-full bg-dig-coral text-white font-bold shrink-0"><Send size={16} className="mr-1" />Send</Button>
      </div>
    </div>
  );
}

/* ---------- Timers ---------- */
function TimersPanel({ code, H, session, call }) {
  const [custom, setCustom] = useState(300);
  const timers = session.settings?.timers || {};
  const start = (label, duration) => call(async () => { await api.post(`/session/${code}/timer`, { action: "start", label, duration }, H); toast.success(`Timer ${label}`); });
  const stop = () => call(() => api.post(`/session/${code}/timer`, { action: "stop" }, H));
  return (
    <div className="max-w-xl bg-white rounded-2xl p-6 dig-card-shadow">
      <h3 className="font-bold mb-4">Timers (server)</h3>
      <div className="grid grid-cols-2 gap-3">
        <Button data-testid="timer-skeptic" onClick={() => start("skeptic", timers.skeptic || 900)} className="rounded-xl bg-dig-blue text-white h-16 flex-col"><Play size={16} /><span>Skeptic {Math.round((timers.skeptic || 900) / 60)}m</span></Button>
        <Button data-testid="timer-producir" onClick={() => start("producir", timers.producir || 1200)} className="rounded-xl bg-dig-turquoise text-dig-harbour h-16 flex-col"><Play size={16} /><span>Producir {Math.round((timers.producir || 1200) / 60)}m</span></Button>
      </div>
      <div className="flex gap-2 mt-3">
        <Input data-testid="timer-custom-input" type="number" value={custom} onChange={(e) => setCustom(e.target.value)} />
        <Button data-testid="timer-custom-start" onClick={() => start("custom", Number(custom))} className="rounded-full bg-dig-yellow text-dig-harbour font-bold shrink-0">Start (s)</Button>
      </div>
      <Button data-testid="timer-stop-btn" onClick={stop} variant="outline" className="mt-3 w-full rounded-full border-dig-coral text-dig-coral"><Square size={14} className="mr-1" />Stop timer</Button>
    </div>
  );
}

/* ---------- Cartas ---------- */
function CardsPanel({ code, H }) {
  const [grouped, setGrouped] = useState({});
  const [edit, setEdit] = useState(null);
  const loadCards = useCallback(() => api.get(`/session/${code}/cards`).then(({ data }) => setGrouped(data.grouped || {})).catch(() => {}), [code]);
  useEffect(() => { loadCards(); }, [loadCards]);
  const del = async (id) => { await api.delete(`/session/${code}/cards/${id}`, H); loadCards(); };
  const save = async (card) => { if (card.id) await api.put(`/session/${code}/cards/${card.id}`, card, H); else await api.post(`/session/${code}/cards`, card, H); setEdit(null); loadCards(); };
  const cats = ["PARTNER", "PAIN", "TRIBE", "TOOL", "TWIST"];
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-dig-harbour/50 text-sm">Codes visible. Add, edit or remove cards (ES/EN).</p>
        <Button data-testid="card-add-btn" onClick={() => setEdit({ category: "PARTNER", code: "", es: "", en: "", label: "PARTNER" })} className="rounded-full bg-dig-yellow text-dig-harbour font-bold"><Plus size={16} className="mr-1" />New card</Button>
      </div>
      <div className="space-y-6">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2"><span className="w-3 h-3 rounded-full" style={{ background: CATEGORY[cat]?.hex }} /><span className="font-bold tracking-widest">{cat}</span><span className="text-dig-harbour/30 text-sm">{(grouped[cat] || []).length}</span></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(grouped[cat] || []).map((c) => (
                <div key={c.id} className="bg-white dig-card-shadow rounded-xl p-3 flex items-start gap-2" data-testid={`card-mgr-${c.code}`}>
                  <span className="font-mono-dig text-xs text-dig-harbour/40 w-12 shrink-0">{c.code}</span>
                  <div className="flex-1 min-w-0"><p className="text-sm truncate">{c.es || <em className="text-dig-harbour/30">blank</em>}</p><p className="text-xs text-dig-harbour/40 truncate">{c.en}</p></div>
                  <button data-testid={`card-edit-${c.code}`} onClick={() => setEdit(c)} className="text-dig-harbour/40 hover:text-dig-blue"><Pencil size={14} /></button>
                  <button data-testid={`card-del-${c.code}`} onClick={() => del(c.id)} className="text-dig-harbour/40 hover:text-dig-coral"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <CardEditor card={edit} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
}

function CardEditor({ card, onClose, onSave }) {
  const [f, setF] = useState(card || {});
  useEffect(() => { setF(card || {}); }, [card]);
  if (!card) return null;
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{card.id ? "Edit card" : "New card"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, label: v })}>
            <SelectTrigger data-testid="card-edit-category"><SelectValue /></SelectTrigger>
            <SelectContent>{["PARTNER", "PAIN", "TRIBE", "TOOL", "TWIST"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="card-edit-code" value={f.code || ""} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="Code (PT-17)" className="font-mono-dig" />
          <Textarea data-testid="card-edit-es" value={f.es || ""} onChange={(e) => setF({ ...f, es: e.target.value })} placeholder="Spanish" />
          <Textarea data-testid="card-edit-en" value={f.en || ""} onChange={(e) => setF({ ...f, en: e.target.value })} placeholder="English" />
        </div>
        <DialogFooter><Button data-testid="card-edit-save" onClick={() => onSave(f)} className="rounded-full bg-dig-blue text-white font-bold">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Investment Leaderboard ---------- */
function Leaderboard({ teams, session, code, H, call }) {
  const ranked = [...teams].sort((a, b) => (b.player_investment || 0) - (a.player_investment || 0));
  const max = Math.max(1, ...ranked.map((t) => t.player_investment || 0));
  const pot = ranked.reduce((a, t) => a + (t.player_investment || 0), 0);
  const isYellow = (hex) => hex === "#FDBB2F";
  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-bold tracking-[0.16em] text-dig-harbour/40">INVESTMENT · LIVE</div>
          <h3 className="text-2xl font-black">Investment board</h3>
        </div>
        <div className="text-right"><div className="text-[10px] tracking-widest text-dig-harbour/40">TOTAL POT</div><div className="font-mono-dig text-2xl font-bold text-dig-turquoise">${pot}</div></div>
      </div>
      {ranked.length === 0 ? <p className="text-dig-harbour/50 text-sm">No teams yet.</p> : (
        <div className="space-y-3">
          {ranked.map((t, i) => (
            <div key={t.id} data-testid={`leaderboard-${t.id}`} className="bg-white rounded-2xl border border-border p-4 dig-card-shadow">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono-dig text-lg font-bold text-dig-harbour/40 w-6">{i + 1}</span>
                <span className="w-3.5 h-3.5 rounded-full" style={{ background: t.color_hex }} />
                <span className="font-bold flex-1 truncate">{t.name}</span>
                {session.winner_team_id === t.id ? <Trophy size={16} className="text-dig-yellow" /> : null}
                <span className="font-mono-dig text-xl font-bold">${t.player_investment || 0}</span>
                <button data-testid={`leaderboard-winner-${t.id}`} onClick={() => call(() => api.post(`/session/${code}/winner`, { team_id: t.id }, H))} title="Mark winner" className="text-dig-harbour/40 hover:text-dig-yellow"><Trophy size={15} /></button>
              </div>
              <div className="h-3 rounded-full bg-dig-harbour-tint overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${((t.player_investment || 0) / max) * 100}%`, background: t.color_hex }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-dig-harbour/40 mt-4">Players invest from their phones during the Pitch and Closing. Budget per player: ${session.settings?.invest_budget ?? 1000}.</p>
    </div>
  );
}

/* ---------- Export ---------- */
function ExportPanel({ code, H, session, call }) {
  const [email, setEmail] = useState(session.settings?.export_email || "");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({
    redraw_limit: session.settings?.redraw_limit ?? 1,
    blank_max_chars: session.settings?.blank_max_chars ?? 120,
    include_partner: session.settings?.include_partner !== false,
    include_pain: session.settings?.include_pain !== false,
    include_tribe: session.settings?.include_tribe !== false,
  });
  const dlUrl = `${BACKEND}/api/session/${code}/export.pdf`;
  const sendEmail = async () => { setBusy(true); try { const { data } = await api.post(`/session/${code}/export/email`, { email }, H); toast.success(`Sent to ${data.to}`); } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setBusy(false); } };
  const saveSettings = () => call(async () => { await api.post(`/session/${code}/settings`, { ...settings, export_email: email }, H); toast.success("Settings saved"); });
  const closeSession = () => call(async () => { await api.post(`/session/${code}/close`, {}, H); toast.success("Session closed"); });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
      <div className="bg-white rounded-2xl p-6 dig-card-shadow">
        <h3 className="font-bold mb-4">Export · PDF</h3>
        <p className="text-sm text-dig-harbour/50 mb-4">One-page summary in DIG style: teams, cards, offer, artifact, twist, investments, winner, commitments.</p>
        <a data-testid="export-download-link" href={dlUrl} target="_blank" rel="noreferrer"><Button className="w-full rounded-full bg-dig-yellow text-dig-harbour font-bold h-12"><Download size={18} className="mr-2" />Download PDF</Button></a>
        <div className="flex gap-2 mt-4">
          <Input data-testid="export-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="report@company.com" />
          <Button data-testid="export-email-btn" onClick={sendEmail} disabled={busy || !email} className="rounded-full bg-dig-blue text-white shrink-0"><Mail size={16} className="mr-1" />Send</Button>
        </div>
      </div>
      <div className="bg-white rounded-2xl p-6 dig-card-shadow">
        <h3 className="font-bold mb-4">Session settings</h3>
        <label className="text-xs text-dig-harbour/50 tracking-widest">CARDS IN THIS GAME</label>
        <div className="mt-2 mb-4 space-y-2">
          {[["include_partner", "Partner"], ["include_pain", "Pain"], ["include_tribe", "Tribe"]].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between bg-dig-paper rounded-xl px-3 py-2">
              <span className="text-sm font-semibold">{label}</span>
              <Switch data-testid={`settings-${key}`} checked={settings[key]} onCheckedChange={(v) => setSettings({ ...settings, [key]: v })} />
            </div>
          ))}
          <div className="flex items-center justify-between bg-dig-paper rounded-xl px-3 py-2 opacity-60">
            <span className="text-sm font-semibold">Tool</span>
            <span className="text-[11px] font-bold tracking-widest text-dig-turquoise">ALWAYS · TEAM PICKS</span>
          </div>
        </div>
        <label className="text-xs text-dig-harbour/50 tracking-widest">REDRAWS ALLOWED</label>
        <Input data-testid="settings-redraw" type="number" min={0} value={settings.redraw_limit} onChange={(e) => setSettings({ ...settings, redraw_limit: Number(e.target.value) })} className="mt-1 mb-3" />
        <label className="text-xs text-dig-harbour/50 tracking-widest">MAX BLANK CARD CHARACTERS</label>
        <Input data-testid="settings-blank" type="number" value={settings.blank_max_chars} onChange={(e) => setSettings({ ...settings, blank_max_chars: Number(e.target.value) })} className="mt-1 mb-4" />
        <Button data-testid="settings-save" onClick={saveSettings} className="w-full rounded-full bg-dig-turquoise text-dig-harbour font-bold mb-3">Save settings</Button>
        <Button data-testid="close-session-btn" onClick={closeSession} variant="outline" className="w-full rounded-full border-dig-coral text-dig-coral">Close session</Button>
      </div>
    </div>
  );
}
